import { api } from "./client";
import { LAST_TENANT_SLUG_KEY, resolveTenantSlug } from "@/lib/tenantSlug";
import type { LoginResponse, User } from "@/types";
import { clearUserScopedState } from "@/store/session";

export async function login(
  email: string,
  password: string,
  totpCode?: string,
): Promise<LoginResponse> {
  // U1a — derive the tenant slug from the host (<slug>.sview360.seple.in),
  // with an email-domain hint only as a fallback for unrecognized hosts. The
  // bare host app.sview360.seple.in and local/preview hosts resolve to
  // "default" (backward-compat). Was hardcoded "default", which made every
  // non-default-tenant user un-loginable. See lib/tenantSlug.ts.
  //
  // An explicit opt-in ?tenant=<slug> overrides the host — the escape hatch for
  // the bare host before per-tenant wildcard DNS exists (e.g.
  // /login?tenant=seple-dev). Honored only when present; never listed/enumerated.
  const host = typeof window !== "undefined" ? window.location.host : "";
  const explicit =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("tenant")
      : null;
  // NO SAVED SLUG. Corrected 2026-10-06. This used to send the slug of the
  // browser's LAST SUCCESSFUL login (localStorage "sview.lastTenantSlug", "Bug 2")
  // and rewrite it only on success. A wrong value therefore could never clear:
  // default-tenant admins were sent to seple-dev 13 times in two weeks and saw
  // "invalid credentials" for a correct password, in that browser, permanently
  // (docs/audit/PILOT-WEEK1-DISCOVERY.md D1-02). The server now resolves the
  // tenant from the email (unique match), so the client sends only what the user
  // can see: an explicit ?tenant= or a real tenant subdomain. Anything else is
  // left to the server.
  //
  // The legacy value is removed on EVERY attempt, before the request, so a
  // FAILED attempt clears it too. A credential hint cached on success and never
  // cleared on failure is the defect class this closes.
  try {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(LAST_TENANT_SLUG_KEY);
    }
  } catch {
    /* localStorage unavailable (private mode): nothing stored to clear */
  }
  // Same rule for everything else the LAST user's session left (the persisted
  // tenant store, the response cache with its signed URLs): cleared before this
  // attempt, so a failed login cannot leave it for the next one (store/session.ts).
  clearUserScopedState();
  const tenant_slug = resolveTenantSlug(host, email, explicit);
  const { data } = await api.post<LoginResponse>("/api/v1/auth/login", {
    email,
    password,
    totp_code: totpCode,
    tenant_slug,
  });
  return data;
}

export async function fetchMe(): Promise<User> {
  const { data } = await api.get<User>("/api/v1/auth/me");
  return data;
}

export async function logout(): Promise<void> {
  try {
    await api.post("/api/v1/auth/logout");
  } catch {
    /* token may already be invalid; ignore */
  }
}
